import React from 'react';
import { Button, Select, MenuItem, InputLabel, Accordion, AccordionSummary, AccordionDetails, Typography, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/PlayCircle';
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import GroupsIcon from '@mui/icons-material/Groups';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import TerrainIcon from '@mui/icons-material/Terrain';
import TimerIcon from '@mui/icons-material/Timer';
import SpeedIcon from '@mui/icons-material/Speed';
import BarChartIcon from '@mui/icons-material/BarChart';
import LiveTvIcon from '@mui/icons-material/LiveTv';

const DisciplineSelect = ({ disciplines }) => {
  const [selectedDiscipline, setSelectedDiscipline] = React.useState('discipline_0');
  const handleChange = (event) => {
    setSelectedDiscipline(event.target.value);
  };

  // discipline が見つからない場合も考慮
  const discipline_0 = disciplines[0];
  const hidden_liveheat = discipline_0?.hidden_liveheat === "1";

  // ▼ ここで進行中のHeat一覧を作る（flatMap を正しく使用）
  const ongoingHeats = disciplines.flatMap((discipline) =>
    discipline.races.flatMap((race) =>
      (race.heats || [])
        .filter((heat) => heat.status === "started")
        .map((heat) => ({
          discipline,
          race,
          heat,
        }))
    )
  );


  return (
    <Box sx={{ marginBottom: '20px' }}>

      {/* ▼ ここが「種目を選択」の上に出る進行中レース一覧（修正版） */}
      {!hidden_liveheat && ongoingHeats.length > 0 && (
        <Box sx={{ marginBottom: 2, padding: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <Typography variant="h4" sx={{ marginBottom: 1 }}>
            現在進行中のレース
          </Typography>

          {ongoingHeats.map(({ discipline, race, heat }, idx) => (
            <Box
              key={idx}
              sx={{
                padding: 1,
                marginBottom: 1.5,
                backgroundColor: '#f7f7f7',
                borderRadius: 1,
              }}
            >
              {/* discipline.name を含めたタイトル行 */}
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {discipline.name} ／ {race.name} ／ {heat.name}
              </Typography>

              {/* 開始時刻 */}
              <Typography variant="caption" sx={{ display: 'block', marginBottom: 0.5 }}>
                Start Time: {heat.started_at ? new Date(heat.started_at).toLocaleString() : "-"}
              </Typography>

              {/* Live ボタン群（renderLiveButtons をそのまま使用） */}
              <Box sx={{ marginTop: 0.5 }}>
                {renderHeatActions(heat, discipline)}
              </Box>
            </Box>
          ))}
        </Box>
      )}


      <InputLabel id="discipline-select-label">種目を選択</InputLabel>
      <Select
        labelId="discipline-select-label"
        id="disciplineSelect"
        value={selectedDiscipline}
        onChange={handleChange}
        sx={{ width: '200px' }}
      >
        {disciplines.map((discipline, index) => (
          <MenuItem key={index} value={`discipline_${index}`}>
            {discipline.name}
          </MenuItem>
        ))}
      </Select>

      <Box id="races">
        {disciplines.map((discipline, disciplineIndex) => (
          <Box
            key={disciplineIndex}
            id={`discipline_${disciplineIndex}`}
            sx={{ display: selectedDiscipline === `discipline_${disciplineIndex}` ? 'block' : 'none' }}
          >
            {discipline.races
              .filter((race) =>
                (discipline.show_hidden !== null ? discipline.show_hidden === "1" : race.public) &&
                !race.soft_destroyed
              )
              .sort((a, b) => a.display_order - b.display_order)
              .map((race, raceIndex) => (
                (discipline.races.length === 1 || discipline.races.filter(race => race.public !== false).length === 1) ? (
                  // racesが1つの場合はAccordionを使わずに表示
                  <Box key={raceIndex}>
                    <Typography variant="h3">{race.name}</Typography>
                    <Box>
                      {race.heats
                        .slice() // 元の配列をコピーして破壊的変更を避ける
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((heat, heatIndex) => (
                          <Box key={heatIndex}>
                            <HeatItem key={heat.id} heat={heat} discipline={discipline} />
                          </Box>
                        ))}
                      {/* Virtualリプレイの最新データを表示 */}
                      {renderLatestVirtual(race, discipline)}
                    </Box>
                  </Box>
                ) : (
                  // racesが複数の場合はAccordionを使用
                  <Accordion key={raceIndex}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${raceIndex}-content`}
                      id={`panel${raceIndex}-header`}
                    >
                      <Typography variant="h3">{race.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {race.heats
                        .slice() // 元の配列をコピーして破壊的変更を避ける
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((heat, heatIndex) => (
                          <Box key={heatIndex}>
                            <HeatItem key={heat.id} heat={heat} discipline={discipline} />
                          </Box>
                        ))}
                      {/* Virtualリプレイの最新データを表示 */}
                      {renderLatestVirtual(race, discipline)}
                    </AccordionDetails>
                  </Accordion>
                )
              ))}
          </Box>
        ))}
      </Box>

    </Box>
  );
};

const HeatItem = ({ heat, discipline }) => {
  // ステータスに応じた文字色を設定する
  const statusColor = heat.status === "completed" ? "blue" : (heat.status === "started" ? "red" : "black");

  return (
    <Box className="heat_item" sx={{
      backgroundColor: '#eee',
      height: '120px',
      margin: '10px',
      borderRadius: '5px',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <Box sx={{
        position: 'absolute',
        left: '10px',
        top: '10%',
      }}>
        <i className="fas fa-flag-checkered fa-2x"></i>
      </Box>
      <Box sx={{
        marginLeft: '60px',
        width: '500px'
      }}>
        <Box sx={{
          fontSize: 'medium',
          fontWeight: '700'
        }}>{heat.name}</Box>
        <Box sx={{
          fontSize: 'x-small',
          fontWeight: '700'
        }}>Start Time: {heat.started_at ? new Date(heat.started_at).toLocaleString() : ""}</Box>
        <Box sx={{
          fontSize: 'small',
          fontWeight: '700',
          color: statusColor // 文字色をステータスに応じて設定
        }}>
          [{heat.status.toUpperCase()}]
        </Box>
      </Box>
      <Box sx={{
        marginLeft: 'auto',
        padding: '0 10px'
      }}>
        {renderHeatActions(heat, discipline)}
      </Box>
    </Box>
  );
};

const renderHeatActions = (heat, discipline) => {
  let resultsUrl = `/heats/${heat.id}/heat_results`; // heat_heat_results_path(heat)に相当するURL
  let dispTitle = "リザルト速報";

  if (discipline.url_options.no_result) {
    if (discipline.url_options.no_result === "1") {
      resultsUrl = `/heats/${heat.id}/athletes`;
      dispTitle = "選手一覧"
    }
  }

  const isAdmin = discipline?.url_options?.admin === "1";

  let admin_view = (
    isAdmin ? (
      <>
        <Button
          href={heat.heat_control_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="error"
          startIcon={<ReplayIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          Heat進行
        </Button>
        {heat.switcher_url && (
          <Button
            href={heat.switcher_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            color="info"
            startIcon={<LiveTvIcon />}
            sx={{
              minWidth: '120px',
              marginRight: 1,
              backgroundColor: '#00bcd4',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#0097a7',
              },
            }}
          >
            スイッチャー
          </Button>
        )}
        <Button
          href={heat.leaderboard_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="success"
          startIcon={<ReplayIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          LeaderBoard
        </Button>
        <Button
          href={heat.greenback_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="success"
          startIcon={<ReplayIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          GreenBack
        </Button>
      </>

    ) : (
      <></>
    )
  );

  const showSwitcher = !isAdmin && (
    discipline.url_options?.broadcast === "1" ||
    (heat.option && heat.option.includes("broadcast=1"))
  );

  const switcherButton = (showSwitcher && heat.switcher_url) ? (
    <Button
      href={heat.switcher_url}
      target="_blank"
      rel="noopener noreferrer"
      variant="contained"
      color="success"
      startIcon={<LiveTvIcon />}
      sx={{
        minWidth: '120px',
        marginRight: 1,
        backgroundColor: '#2e7d32',
        '&:hover': {
          backgroundColor: '#1b5e20',
        },
      }}
    >
      スイッチャー
    </Button>
  ) : null;

  if (heat.status === "completed") {
    if (heat.heat_replays.length === 0) {
      return (
        <>
          {switcherButton}
          {admin_view}
          <Button startIcon={<GroupsIcon />} sx={{ minWidth: '120px', marginRight: 1 }} variant="contained" color="primary" href={resultsUrl} target="_blank" rel="noopener noreferrer">選手一覧</Button>
          <Button startIcon={<DoDisturbIcon />} sx={{ minWidth: '120px', marginRight: 1 }} variant="contained" style={{ backgroundColor: 'gray', color: 'white' }} disabled>No Replay</Button>
        </>
      );
    } else {
      return (
        <>
          {switcherButton}
          {admin_view}
          {renderReplayButtons(heat, discipline)}
          <Button startIcon={<LeaderboardIcon />} sx={{ minWidth: '120px', marginRight: 1 }} variant="contained" color="primary" href={resultsUrl} target="_blank" rel="noopener noreferrer">{dispTitle}</Button>
        </>
      );
    }
  } else if (heat.status === "started") {
    return (
      <>
        {switcherButton}
        {admin_view}
        {renderLiveButtons(heat, discipline)}
        <Button startIcon={<LeaderboardIcon />} sx={{ minWidth: '120px', marginRight: 1 }} variant="contained" color="primary" href={resultsUrl} target="_blank" rel="noopener noreferrer">{dispTitle}</Button>
      </>
    );
  } else {
    return (
      <>
        {switcherButton}
        {admin_view}
        <Button startIcon={<GroupsIcon />} sx={{ minWidth: '120px', marginRight: 1 }} variant="contained" color="primary" href={resultsUrl} target="_blank" rel="noopener noreferrer">選手一覧</Button>
        <Button startIcon={<DoDisturbIcon />} sx={{ minWidth: '120px', marginRight: 1 }} variant="contained" style={{ backgroundColor: 'gray', color: 'white' }} disabled>Not yet...</Button>
      </>
    );
  }
};



const renderReplayButtons = (heat, discipline) => {

  // available? が true のリプレイを抽出
  const availableReplays = heat.heat_replays.filter(r => r["available?"]);

  // availableなものがあればその最新(最後)、なければ全体のエントリの最新(最後)を使用
  const replay = availableReplays.length > 0
    ? availableReplays[availableReplays.length - 1]
    : heat.heat_replays[heat.heat_replays.length - 1];

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<ReplayIcon />}
        href={replay.broadcast_url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ minWidth: '120px', marginRight: 1 }}
      >
        Replay
      </Button>

      {discipline.type.name.toLowerCase() === "sailing" && replay.url && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<ThreeDRotationIcon />}
          href={`${replay.broadcast_url}&view=terrain`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          Replay
        </Button>
      )}

      {(discipline.url_options.admin === "1" || !discipline.url_options.no_result) &&
        <Button
          href={heat.laptime_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="primary"
          startIcon={<TimerIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          Lap
        </Button>
      }

      {(discipline.url_options.admin === "1" || discipline.url_options.ave_spd === "1") &&
        <Button
          href={`${heat.laptime_url}/speed`}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="info"
          startIcon={<SpeedIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          平均速度
        </Button>
      }

    </>
  );
};


const renderLiveButtons = (heat, discipline) => {
  return (
    <>
      <Button
        href={heat.map_url}
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        color="error"
        startIcon={<ReplayIcon />}
        sx={{ minWidth: '120px', marginRight: 1 }}
      >
        Live
      </Button>

      {discipline.type.name.toLowerCase() === "sailing" && (
        <Button
          href={`${heat.map_url}&view=terrain`}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="error"
          startIcon={<ThreeDRotationIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          Live
        </Button>
      )}

      {discipline.type.name.toLowerCase() === "roadrace" && (
        <Button
          href={`${heat.terrain_url}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="error"
          startIcon={<ThreeDRotationIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          Live
        </Button>
      )}



      {(discipline.url_options.admin === "1" || !discipline.url_options.no_result) &&
        <Button
          href={heat.laptime_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="primary"
          startIcon={<TimerIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          Lap
        </Button>
      }

      {(discipline.url_options.admin === "1" || discipline.url_options.ave_spd === "1") &&
        <Button
          href={`${heat.laptime_url}/speed`}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          color="info"
          startIcon={<SpeedIcon />}
          sx={{ minWidth: '120px', marginRight: 1 }}
        >
          平均速度
        </Button>
      }

    </>
  );
};

// raceのvirtualsから最新のavailableなvirtualを取得して表示
const renderLatestVirtual = (race, discipline) => {
  if (!race.virtuals || race.virtuals.length === 0) return null;

  // availableなvirtualを抽出し、idが最大（最新）のものを取得
  const availableVirtuals = race.virtuals.filter(v => v.status === 'available');
  if (availableVirtuals.length === 0) return null;

  const latestVirtual = availableVirtuals[availableVirtuals.length - 1];
  return <VirtualItem virtual={latestVirtual} discipline={discipline} />;
};

const VirtualItem = ({ virtual, discipline }) => {
  // admin=1の場合は全表示、no_result=1の場合はStatsボタンを非表示
  const isAdmin = discipline?.url_options?.admin === "1";
  const isNoResult = discipline?.url_options?.no_result === "1";
  const showStatsButton = isAdmin || !isNoResult;
  return (
    <Box className="heat_item" sx={{
      backgroundColor: '#e8f0fe',
      height: '120px',
      margin: '10px',
      borderRadius: '5px',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <Box sx={{
        position: 'absolute',
        left: '10px',
        top: '10%',
      }}>
        <i className="fas fa-play-circle fa-2x"></i>
      </Box>
      <Box sx={{
        marginLeft: '60px',
        width: '500px'
      }}>
        <Box sx={{
          fontSize: 'medium',
          fontWeight: '700'
        }}>{virtual.message || 'Virtual Replay'}</Box>
        <Box sx={{
          fontSize: 'small',
          fontWeight: '700',
          color: 'purple'
        }}>
          [VIRTUAL]
        </Box>
      </Box>
      <Box sx={{
        marginLeft: 'auto',
        padding: '0 10px'
      }}>
        {virtual.broadcast_url && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<ReplayIcon />}
            href={virtual.broadcast_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: '120px', marginRight: 1 }}
          >
            Replay
          </Button>
        )}
        {showStatsButton && virtual.stats_broadcast_url && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<BarChartIcon />}
            href={virtual.stats_broadcast_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: '120px', marginRight: 1 }}
          >
            Stats
          </Button>
        )}
      </Box>
    </Box>
  );
};


export default DisciplineSelect;

